import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Input Component
 *
 * A flexible, accessible input field component built on shadcn/ui standards.
 * Provides consistent styling, proper accessibility, and theme awareness.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Input type="email" placeholder="Enter your email" />
 *
 * // File input
 * <Input type="file" accept="image/*" />
 *
 * // Disabled state
 * <Input type="text" disabled value="Read only" />
 *
 * // With custom styling
 * <Input type="text" className="border-red-500" placeholder="Custom styling" />
 * ```
 */
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(({ className, type, ...props }, ref) => {
    return (
        <input
            type={type}
            data-slot="input"
            className={cn(
                "setu:file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground setu:!border-input setu:flex setu:h-9 setu:w-full setu:min-w-0 setu:rounded-md setu:!border setu:bg-background setu:px-3 setu:py-1 setu:text-base setu:shadow-xs setu:transition-[color,box-shadow] setu:outline-none setu:file:inline-flex setu:file:h-7 setu:file:!border-0 setu:file:bg-transparent setu:file:text-sm setu:file:font-medium setu:disabled:pointer-events-none setu:disabled:cursor-not-allowed setu:disabled:opacity-50 setu:md:text-sm setu:focus-visible:!border-ring setu:focus-visible:ring-ring/50 setu:focus-visible:ring-[3px] setu:aria-invalid:ring-destructive/20 dark:setu:aria-invalid:ring-destructive/40 setu:aria-invalid:!border-destructive",
                className
            )}
            ref={ref}
            {...props}
        />
    );
});
Input.displayName = "Input";

export { Input };
